// Project Admin — Project Letter Generator (PDF page 13)
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { wsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  PageShell, PageHead, Card, Field, Input, Select, Textarea, Stepper, DotFlow, FileDrop, FileChip, Spin, Tabs, Empty, Loading, ReqStatus,
  useAsync, useProjects, useQueryParam, uploadFiles, money, fmtDate, today, BUSINESS_UNITS,
} from '../../components/ui';
import RequestDetail from '../../components/RequestDetail';
import { notify } from '../../utils/notify';
import { downloadElementPDF } from '../../utils/elementPDF';

const TEMPLATES = {
  'Delivery extension request': 'We request your review of the revised delivery schedule for the referenced project.\nSupporting details are attached for approval.',
  'Drawing submission': 'Please find enclosed the drawings listed in the attached transmittal for your review and approval.\nKindly share your comments at the earliest to maintain the project schedule.',
  'Payment follow-up': 'This is with reference to the invoices raised against the referenced project which are pending for payment.\nWe request you to kindly release the outstanding amount at the earliest.',
  'Site handover': 'We are pleased to inform you that the works under the referenced project have been completed.\nWe request you to arrange the joint inspection and take over of the site.',
  'Meeting request': 'We would like to request a review meeting to discuss the progress of the referenced project.\nKindly confirm a suitable date and time.',
  'General letter': '',
};
const DESIGNATIONS = ['Project Manager', 'General Manager', 'Chief Engineer', 'Plant Head', 'Purchase Manager', 'Director'];
const SIGNATORIES = ['Business Head', 'Project Manager', 'Head – Engineering', 'Chief Executive Officer'];
const STAGES = [{ label: 'Draft' }, { label: 'Project review' }, { label: 'Signatory approval' }, { label: 'Controlled issue' }];

const blank = (u) => ({
  template: 'Delivery extension request', project: '', client: '', date: today(), subject: 'Request for delivery schedule extension',
  recipientDesignation: 'Project Manager', signatory: 'Business Head', body: TEMPLATES['Delivery extension request'], businessUnit: u?.businessUnit || 'Water Business Unit',
});

export default function ProjectLetters() {
  const { user } = useAuth();
  const projects = useProjects();
  const openId = useQueryParam('id');
  const [tab, setTab] = useState('compose');
  const [f, setF] = useState(blank(user));
  const [current, setCurrent] = useState(null); // loaded request
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(null);
  const [detail, setDetail] = useState(openId);
  const previewRef = useRef();
  const mine = useAsync(() => wsAPI.listRequests({ type: 'project_letter', scope: 'mine' }), []);
  const involved = useAsync(() => wsAPI.listRequests({ type: 'project_letter', scope: 'all' }), []);
  const clients = useMemo(() => [...new Set(projects.map((p) => p.client).filter(Boolean))], [projects]);

  useEffect(() => { if (openId) setDetail(openId); }, [openId]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const pickTemplate = (t) => setF((p) => ({ ...p, template: t, body: TEMPLATES[t] ?? p.body, subject: t === 'Delivery extension request' ? 'Request for delivery schedule extension' : p.subject }));
  const pickProject = (code) => {
    const pr = projects.find((x) => x.projectCode === code);
    setF((p) => ({ ...p, project: code, client: pr?.client || p.client }));
  };

  const load = (r) => {
    setCurrent(r);
    setF({ ...blank(user), ...(r.data || {}), project: r.project || '', subject: r.title, date: r.data?.date || today() });
    setFiles([]);
    setTab('compose');
  };
  const newLetter = () => { setCurrent(null); setF(blank(user)); setFiles([]); };

  const filled = f.template && f.project && f.client && f.date && f.subject && f.recipientDesignation && f.signatory;
  const status = current?.status;
  const step = !current || ['DRAFT', 'RETURNED'].includes(status) ? (filled ? 2 : f.template ? 1 : 0) : 3;
  const stageIndex = !current || status === 'DRAFT' || status === 'RETURNED' ? 0 : status === 'PENDING' ? 1 + Math.min(2, current.currentStage) : status === 'APPROVED' ? 4 : 0;
  const editable = !current || ['DRAFT', 'RETURNED'].includes(status);
  const issued = current?.status === 'APPROVED';

  const save = async (submit) => {
    if (submit && !filled) { notify.error('Fill all required fields'); return; }
    if (!f.subject.trim()) { notify.error('Subject is required'); return; }
    setSaving(submit ? 'submit' : 'draft');
    try {
      const attachments = [...(current?.attachments || []), ...(files.length ? await uploadFiles(files, 'workspace') : [])];
      const body = {
        type: 'project_letter', title: f.subject, project: f.project, businessUnit: f.businessUnit, attachments,
        data: { template: f.template, client: f.client, date: f.date, recipientDesignation: f.recipientDesignation, signatory: f.signatory, body: f.body, businessUnit: f.businessUnit },
        submit,
      };
      const r = current ? await wsAPI.updateRequest(current._id, body) : await wsAPI.createRequest(body);
      setCurrent(r.request);
      setFiles([]);
      notify.success(submit ? `${r.request.refNo} sent for approval` : `Draft saved — ${r.request.refNo}`);
      mine.reload(); involved.reload();
    } catch (e) { notify.error(e.message || 'Could not save'); } finally { setSaving(null); }
  };

  const downloadPdf = async () => {
    try { await downloadElementPDF(previewRef.current, `${current?.refNo || 'project-letter'}.pdf`); } catch (e) { notify.error(e.message); }
  };

  const ref = current?.refNo || `TSE-${(f.businessUnit || 'Water Business Unit').split(' ').map((w) => w[0]).join('')}-LTR-${new Date().getFullYear()}-XXXX`;
  const pendingMine = (involved.data?.requests || []).filter((r) => r.canAct);

  return (
    <PageShell section="Project Admin">
      <PageHead title="Project Letter Generator" subtitle="Create and manage project letters with the required approvals"
        actions={<span className="tse-version-pill">Version {current?.data?.version || 'R0'} &nbsp;•&nbsp; {issued ? 'Issued' : 'Not issued'}</span>} />

      <div className="tse-row mb-18" style={{ justifyContent: 'space-between' }}>
        <Tabs value={tab} onChange={setTab} tabs={[{ key: 'compose', label: current ? `Letter ${current.refNo}` : 'New letter' }, { key: 'mine', label: 'My letters', count: (mine.data?.requests || []).length }, { key: 'review', label: 'For my approval', count: pendingMine.length }, { key: 'register', label: 'Letter register' }]} />
        {tab === 'compose' && current && <button type="button" className="tse-btn outline sm" onClick={newLetter}><i className="bi bi-plus-lg" />New letter</button>}
      </div>

      {tab === 'compose' && (
        <>
          <div className="mb-18" style={{ padding: '0 42px' }}><Stepper steps={['Select template', 'Enter details', 'Review', 'Approve & issue']} current={step} /></div>
          <div className="tse-split half">
            <div>
              <Card title="Letter Details" sub="Fill in the details below to generate your project letter.">
                <div className="tse-stack" style={{ gap: 8 }}>
                  <Field label="Template" required><Select value={f.template} onChange={(e) => pickTemplate(e.target.value)} options={Object.keys(TEMPLATES)} disabled={!editable} /></Field>
                  <div className="tse-form-grid c2">
                    <Field label="Project" required><Select value={f.project} onChange={(e) => pickProject(e.target.value)} placeholder="Select project" disabled={!editable} options={projects.map((p) => ({ value: p.projectCode, label: `${p.projectCode} — ${p.name}` }))} /></Field>
                    <Field label="Client" required>
                      <Input list="letter-clients" value={f.client} onChange={(e) => set('client', e.target.value)} disabled={!editable} placeholder="Client name" />
                      <datalist id="letter-clients">{clients.map((c) => <option key={c} value={c} />)}</datalist>
                    </Field>
                  </div>
                  <Field label="Document reference" required hint={current ? undefined : 'Assigned automatically when you save'}><Input value={ref} readOnly /></Field>
                  <Field label="Date" required><Input type="date" value={f.date} onChange={(e) => set('date', e.target.value)} disabled={!editable} /></Field>
                  <Field label="Subject" required><Input value={f.subject} onChange={(e) => set('subject', e.target.value)} disabled={!editable} /></Field>
                  <div className="tse-form-grid c2">
                    <Field label="Recipient designation" required><Select value={f.recipientDesignation} onChange={(e) => set('recipientDesignation', e.target.value)} options={DESIGNATIONS} disabled={!editable} /></Field>
                    <Field label="Authorized signatory" required><Select value={f.signatory} onChange={(e) => set('signatory', e.target.value)} options={SIGNATORIES} disabled={!editable} /></Field>
                  </div>
                  <Field label="Business unit"><Select value={f.businessUnit} onChange={(e) => set('businessUnit', e.target.value)} options={BUSINESS_UNITS} disabled={!editable} /></Field>
                  <Field label="Letter body"><Textarea rows={5} value={f.body} onChange={(e) => set('body', e.target.value)} disabled={!editable} /></Field>
                  <Field label="Attach supporting documents">
                    {editable && <FileDrop inline onFiles={(fs) => setFiles((p) => [...p, ...fs])} />}
                    {(current?.attachments || []).map((a, i) => <FileChip key={a.s3Key || i} file={a} />)}
                    {files.map((file, i) => <FileChip key={`n${i}`} file={file} onRemove={() => setFiles((p) => p.filter((_, j) => j !== i))} />)}
                  </Field>
                </div>
              </Card>
              <div className="tse-row" style={{ marginTop: 8, gap: 8 }}>
                {editable && <button type="button" className="tse-btn lg outline" style={{ minWidth: 150 }} onClick={() => save(false)} disabled={!!saving}>{saving === 'draft' ? <Spin /> : null}Save draft</button>}
                {editable && <button type="button" className="tse-btn lg" style={{ minWidth: 180 }} onClick={() => save(true)} disabled={!!saving}>{saving === 'submit' ? <Spin /> : null}Send for approval</button>}
                {current && !editable && <button type="button" className="tse-btn lg outline" onClick={() => setDetail(current._id)}><i className="bi bi-diagram-3" />Approval details</button>}
                {current && <button type="button" className="tse-btn lg gray" onClick={downloadPdf}><i className="bi bi-download" />{issued ? 'Download issued letter' : 'Download draft PDF'}</button>}
              </div>
            </div>

            <Card title="Letter Preview" sub="This is a preview of your letter based on the details provided.">
              <div ref={previewRef} className="tse-letter">
                {!issued && <div className="wm">DRAFT</div>}
                <div className="tse-row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div><div className="brand">TSE</div><div className="bu">{f.businessUnit}</div></div>
                  <div className="addr">TSE Infrastructure Pvt. Ltd.<br />{f.businessUnit}<br />New Delhi – 110001, India<br />{fmtDate(f.date)}</div>
                </div>
                <div style={{ marginTop: 8 }}>Ref: {ref}</div>
                <div style={{ marginTop: 8 }}>To,<br />The {f.recipientDesignation}<br />{f.client || '[Client]'}</div>
                <div style={{ marginTop: 8 }}><strong>Subject: {f.subject || '[Subject]'}</strong>{f.project ? ` — Project ${f.project}` : ''}</div>
                <div style={{ marginTop: 8 }}>Dear Sir/Madam,</div>
                <div style={{ marginTop: 10, whiteSpace: 'pre-wrap' }}>{f.body || ' '}</div>
                <div style={{ marginTop: 8 }}>Yours faithfully,</div>
                <div style={{ marginTop: 8 }}><strong>Authorized Signatory</strong><br />{f.signatory}<br />TSE Infrastructure Pvt. Ltd.<br />{f.businessUnit}</div>
                {issued && <div style={{ marginTop: 8, fontSize: '0.718rem', color: '#1e8a5a' }}>Controlled copy · issued {fmtDate(current.completedAt)} · {current.refNo} · {current.data?.version || 'R0'}</div>}
              </div>
              <div className="tse-row" style={{ background: '#f4f7fa', borderRadius: 8, padding: '10px 13px', marginTop: 8, gap: 8, flexWrap: 'nowrap' }}>
                <strong style={{ whiteSpace: 'nowrap' }}>Approval Path</strong>
                <div style={{ flex: 1 }}><DotFlow nodes={STAGES.map((s, i) => ({ label: s.label, state: i < stageIndex ? 'done' : i === stageIndex ? 'active' : '' }))} /></div>
              </div>
              {current?.status === 'RETURNED' && <div className="tse-note warn" style={{ marginTop: 8 }}><i className="bi bi-exclamation-circle-fill" /><div><div className="t">Returned</div>{(current.history || []).filter((h) => h.action === 'RETURNED').slice(-1)[0]?.remarks}</div></div>}
            </Card>
          </div>
        </>
      )}

      {tab !== 'compose' && (() => {
        const rows = tab === 'mine' ? (mine.data?.requests || []) : tab === 'review' ? pendingMine : (involved.data?.requests || []);
        const loading = tab === 'mine' ? mine.loading : involved.loading;
        return (
          <Card bodyClass="flush">
            {loading ? <Loading /> : !rows.length ? <Empty icon="bi-envelope-paper" title="No letters here" /> : (
              <div className="tse-table-wrap"><table className="tse-table">
                <thead><tr><th>Reference</th><th>Subject</th><th>Project</th><th>Client</th><th>Requested by</th><th>Status</th><th>Updated</th><th /></tr></thead>
                <tbody>{rows.map((r) => (
                  <tr key={r._id} className="clickable" onClick={() => (r.isOwner ? load(r) : setDetail(r._id))}>
                    <td className="strong">{r.refNo}</td><td>{r.title}</td><td>{r.project}</td><td>{r.data?.client}</td><td>{r.requesterName}</td>
                    <td><ReqStatus request={r} /></td><td>{fmtDate(r.updatedAt)}</td>
                    <td onClick={(e) => e.stopPropagation()}><button type="button" className="tse-btn xs outline" onClick={() => setDetail(r._id)}>{r.canAct ? 'Review' : 'Details'}</button></td>
                  </tr>
                ))}</tbody>
              </table></div>
            )}
          </Card>
        );
      })()}

      <RequestDetail id={detail} onClose={() => setDetail(null)} onChanged={() => { mine.reload(); involved.reload(); }}
        extra={(r) => (
          <div className="tse-letter" style={{ minHeight: 0, padding: 14, fontSize: '0.773rem' }}>
            <strong>Subject: {r.title}</strong><div style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>{r.data?.body}</div>
            <div style={{ marginTop: 10 }} className="tse-muted">To {r.data?.recipientDesignation}, {r.data?.client} · Signatory: {r.data?.signatory}{r.amount ? ` · ${money(r.amount)}` : ''}</div>
          </div>
        )} />
    </PageShell>
  );
}
